/**
 * SHER-211 — minimal per-tenant translation overlay.
 *
 * Tenants need to override individual labels without forking the whole
 * global.json. CHOM Dakar's first ask was renaming "ID de santé" to "Dossier
 * CHOM" so clinicians stop confusing it with the legacy CHOM MRN, but the same
 * mechanism will be reused for any future tenant-specific copy change.
 *
 * Mechanism:
 *   1. ProviderSherwood derives the tenant slug from `VITE_APP_API_URL` via
 *      `resolveTenantFromApiUrl()` — every tenant already has its own API host
 *      in that env var, so we don't need a separate `VITE_APP_TENANT` that has
 *      to be remembered in every deploy (forgetting it was the reason the
 *      first cut of this feature didn't take effect on CHOM).
 *   2. ProviderSherwood deep-merges `tenant/<slug>.json` into the base global
 *      translations before LocalizeProvider initialises.
 *   3. If the API host doesn't match a known tenant, base global.json is used
 *      unchanged — that's the path for localhost, staging, and any tenant we
 *      haven't registered an overlay for.
 *
 * Translations in this codebase are stored as 3-language arrays
 * `[en, es, fr]` at the leaves of a nested object tree. We deep-merge nested
 * objects but treat arrays as atomic leaves — overlaying an array fully
 * replaces the base array, never merges element-by-element. That's the safest
 * default for the [en, es, fr] convention: a tenant overlay either replaces
 * all three languages or leaves the leaf untouched.
 */

type Translations = Record<string, any>;

function isPlainObject(v: any): v is Record<string, any> {
    return v != null && typeof v === 'object' && !Array.isArray(v);
}

export function applyTenantOverlay<T extends Translations>(base: T, overlay: any): T {
    if (!isPlainObject(overlay)) return base;
    const out: Translations = { ...base };
    for (const key of Object.keys(overlay)) {
        const baseVal = (base as any)[key];
        const overlayVal = overlay[key];
        if (isPlainObject(baseVal) && isPlainObject(overlayVal)) {
            out[key] = applyTenantOverlay(baseVal, overlayVal);
        } else {
            // Arrays (translation leaves) and primitives are replaced atomically.
            out[key] = overlayVal;
        }
    }
    return out as T;
}

/**
 * Vite handles dynamic-import of JSON via the bundler, so we can't pick the
 * overlay file by string-concatenating the tenant slug at runtime — Vite needs
 * the literal specifier at build time. Caller passes the (statically-imported)
 * overlay module via this registry; keeping the registry small and explicit
 * also forces a conscious decision when a new tenant is added.
 */
export interface TenantOverlayRegistry {
    [tenantSlug: string]: Translations;
}

export function pickTenantOverlay(
    registry: TenantOverlayRegistry,
    tenant: string | undefined | null,
): Translations | null {
    if (!tenant) return null;
    return registry[tenant] ?? null;
}

/**
 * Maps a known production API host to its tenant slug. Each tenant deployment
 * already configures its own `VITE_APP_API_URL` in the build's .env file, so
 * we reuse that to identify the tenant — no extra env var to forget.
 *
 * Hosts not listed here (localhost, staging, generic api.sherwood.science,
 * tenants without an overlay) resolve to null and the base translations are
 * used unchanged.
 */
const API_HOST_TO_TENANT: Record<string, string> = {
    'chom-dakar-api.sherwood.science': 'chom-dakar',
};

export function resolveTenantFromApiUrl(apiUrl: string | undefined | null): string | null {
    if (!apiUrl) return null;
    let host: string;
    try {
        host = new URL(apiUrl).host;
    } catch {
        return null;
    }
    if (!host) return null;
    return API_HOST_TO_TENANT[host] ?? null;
}

/**
 * SHER-371 — compose the resolution steps (slug → overlay → deep-merge) into a
 * single call so consumers outside the React/redux tree can reproduce the exact
 * per-tenant label overrides the LocalizeProvider applies at app init.
 *
 * The PDF renderer (`components/pdf/utils/simpleTranslate`) reads its
 * translations straight from `global.json`, bypassing redux entirely, so it
 * relies on this helper to honour overrides such as CHOM Dakar's "Dossier
 * CHOM". The `registry` is passed in (not hard-coded here) to keep this module
 * free of any tenant-overlay JSON imports — callers own their registry.
 */
export function resolveTenantTranslations<T extends Translations>(
    base: T,
    apiUrl: string | undefined | null,
    registry: TenantOverlayRegistry,
): T {
    const slug = resolveTenantFromApiUrl(apiUrl);
    const overlay = pickTenantOverlay(registry, slug);
    return overlay ? applyTenantOverlay(base, overlay) : base;
}
